import { convertToGroupTagId, ORDER_TYPE } from '@exam-management/common/utils';
import { useSearchExamCoursesQuery } from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import { type GridCellParams, DataGrid, ErrorBox, NoRowsOverlay } from '@shared/components';

import { useExamCourseSearchParams } from '../../hooks';

import { columns } from './columns';

export default function ExaminationCourseList() {
  const navigate = useNavigate();

  const [{ search, page, pageSize, sort, field, group }, setExamCourseSearchParams] =
    useExamCourseSearchParams();

  const {
    data,
    loading: isLoading,
    error,
  } = useSearchExamCoursesQuery({
    variables: {
      searchExamCoursesInput: {
        order: {
          field,
          type: ORDER_TYPE[sort],
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        filter: search
          ? {
              searchText: {
                fields: ['name', 'code'],
                value: search,
              },
            }
          : undefined,
        groupTagId: convertToGroupTagId(group),
      },
    },
  });

  const examCourses = data?.excSearchExamCourses.examCourses ?? [];
  const examCoursesTotal = data?.excSearchExamCourses.total ?? 0;

  const handleCellClick = (cell: GridCellParams) => {
    if (cell.field !== 'actions') {
      // NOTE: If redirect to exam course details page, we cannot navigate back to this page with the browser history.
      navigate('/exam-courses/:courseId', { params: { courseId: cell.row.id } });
    }
  };

  if (error) {
    return <ErrorBox />;
  }

  return (
    <DataGrid
      rows={examCourses}
      columns={columns}
      onCellClick={handleCellClick}
      getRowHeight={() => 'auto'}
      loading={isLoading}
      slots={{
        noRowsOverlay: NoRowsOverlay,
      }}
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        '.MuiDataGrid-cell': {
          padding: theme.spacing(3),
          paddingBottom: theme.spacing(2.75),
        },
        '.MuiDataGrid-columnHeader': {
          backgroundColor: theme.palette.grey[100],
          padding: theme.spacing(3),
        },
        '.MuiDataGrid-cell:last-child': {
          padding: theme.spacing(1.5),
        },
      })}
      disableColumnMenu
      hideFooter={examCoursesTotal === 0}
      rowSelection={false}
      slotProps={{
        footer: {
          total: examCoursesTotal,
          pagination: {
            page,
            count: examCoursesTotal,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              setExamCourseSearchParams({
                page,
              });
            },
            onRowsPerPageChange: (event) => {
              const pageSize = parseInt(event.target.value);
              return setExamCourseSearchParams({
                pageSize,
                page: 0,
              });
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const field = sorting[0]?.field;
        const sort = sorting[0]?.sort ?? undefined;
        if (field && sort) {
          setExamCourseSearchParams({
            field,
            sort,
          });
        }
      }}
    />
  );
}
